/**
 * Side nav structure (#939, Feature C). Config-driven so adding a section (MCP Servers,
 * My Connections) is a one-line change here, and so the active-item mapping is unit-testable
 * without rendering AppContent.
 */

import { helpTopicForSection, type Route } from "./routing";

export type NavItemId = "skills" | "mcp" | "myskills" | "contribute" | "admin" | "help" | "help-mcp";
export type NavIcon = "grid" | "server" | "user" | "plus" | "sliders" | "help";

export interface NavItem {
  id: NavItemId;
  label: string;
  icon: NavIcon;
  /** Shown only to Marketplace admins (the same check that gates the Admin page). */
  adminOnly?: boolean;
  /** Shown only when the MCP Servers section is on (REACT_APP_MCP_SERVERS_ENABLED). */
  mcpServersOnly?: boolean;
}

export interface NavVisibility {
  isAdmin: boolean;
  mcpServersEnabled: boolean;
}

export interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Discover",
    items: [
      { id: "skills", label: "Skills & Plugins", icon: "grid" },
      { id: "mcp", label: "MCP Servers", icon: "server", mcpServersOnly: true },
    ],
  },
  {
    label: "Yours",
    items: [
      { id: "myskills", label: "My Skills", icon: "user" },
      { id: "contribute", label: "Contribute", icon: "plus" },
    ],
  },
  { label: "Platform tools", items: [{ id: "admin", label: "Admin", icon: "sliders", adminOnly: true }] },
  {
    label: "Help",
    items: [
      { id: "help", label: "Skills Help", icon: "grid" },
      { id: "help-mcp", label: "MCP Help", icon: "server", mcpServersOnly: true },
    ],
  },
];

/** The groups this user should see. Hidden items are removed, not shown disabled; a group left empty is dropped. */
export function visibleNavGroups(
  { isAdmin, mcpServersEnabled }: NavVisibility,
  groups: readonly NavGroup[] = NAV_GROUPS,
): NavGroup[] {
  const visible = (item: NavItem) => (!item.adminOnly || isAdmin) && (!item.mcpServersOnly || mcpServersEnabled);
  return groups
    .map((group) => ({ ...group, items: group.items.filter(visible) }))
    .filter((group) => group.items.length > 0);
}

/** Which nav item a route belongs to, so the section stays highlighted on its detail and edit pages too. */
export function activeNavItemId(route: Route): NavItemId {
  switch (route.page) {
    case "catalog":
    case "detail":
      return "skills";
    case "myskills":
      return "myskills";
    case "edit":
      // Editing opened from My Skills belongs to My Skills; otherwise it's part of Contribute.
      return route.from === "myskills" ? "myskills" : "contribute";
    case "contribute":
      return "contribute";
    case "admin":
      return "admin";
    case "help":
      return route.topic === "mcp" || (route.section !== undefined && helpTopicForSection(route.section) === "mcp")
        ? "help-mcp"
        : "help";
    case "mcp":
    case "mcpserver":
      return "mcp";
    default: {
      // Exhaustiveness guard: a new Route variant must map to a nav item here.
      const _exhaustive: never = route;
      return _exhaustive;
    }
  }
}
